// src/utils/topicKey.js
//
// ONE place that decides which document a topic's tools live under.
//
// THE BUG THIS FIXES
// ------------------
// Every tool used to strip a slug down to letters and digits before using it
// as a key: `earth'swater` and `earthswater` both became `earthswater`. Two
// different topics then shared ONE game (quizzes/, generatedGames/), ONE
// sing-along (lyricSync/), ONE set of upload filenames
// (videos/earthswater2026video.mp4), and Topic Library saves for one landed
// on the OTHER topic's library document. Adding tools to one blocked or
// overwrote the other.
//
// THE RULE
// --------
//   * topicLibrary/{id} is always addressed by its REAL document id. Never
//     re-derived from the slug.
//   * Tool keys (games, drafts, sing-alongs, filenames) keep the old stripped
//     key for every topic, so nothing that already works moves, EXCEPT when
//     stripping would land on a DIFFERENT library topic. Only then does the
//     topic get its own exact key. That is the "twin" case.
//
//       earthswater   -> earthswater      (unchanged)
//       earth'swater  -> earth'swater     (its twin exists, so it gets its own)
//       water-cycle   -> watercycle       (no twin: unchanged, old data stays put)
//
//   * Readers try the exact key first and the stripped key second. A twin
//     with no game of its own yet keeps showing the shared one until you
//     publish one for it; then each topic has its own.
//
// KEEP IN STEP WITH src/scripts/align-lyrics.py (tool_key_for).

/** The library document id for a slug: lowercased and trimmed, nothing removed. */
export const exactKey = (value = "") =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    // "/" is the only character a document id cannot hold.
    .replace(/\//g, "_");

/** The old key: letters and digits only. What every existing tool doc uses. */
export const legacyKey = (value = "") =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/** Keys to try when READING a tool document, best first, no duplicates. */
export const keyCandidates = (value = "") => {
  const out = [];
  [exactKey(value), legacyKey(value)].forEach((k) => {
    if (k && !out.includes(k)) out.push(k);
  });
  return out;
};

/** The real library id of a topic object ({ id, slug }). */
export const libraryIdOf = (topic = {}) => exactKey(topic?.id || topic?.slug || "");

/**
 * Builds the tool-key function for one snapshot of the library.
 *
 *   const toolKey = makeToolKey(topics);
 *   toolKey("earth'swater")  // "earth'swater" — because earthswater exists
 *
 * `topics` may be topic objects or plain slugs.
 */
export const makeToolKey = (topics = []) => {
  const ids = new Set(
    (topics || [])
      .map((t) => (typeof t === "string" ? exactKey(t) : libraryIdOf(t)))
      .filter(Boolean)
  );
  return (slugOrTopic) => {
    const raw =
      typeof slugOrTopic === "string"
        ? slugOrTopic
        : slugOrTopic?.id || slugOrTopic?.slug || "";
    const exact = exactKey(raw);
    const legacy = legacyKey(raw);
    if (!exact) return "";
    if (!legacy || exact === legacy) return exact;
    // Stripping would land on ANOTHER library topic: keep this one separate.
    return ids.has(legacy) ? exact : legacy;
  };
};

/**
 * A tool key made safe for a Storage filename. Identical to the key for every
 * ordinary topic; a twin's punctuation becomes "_", so earth'swater uploads as
 * earth_swater2026video.mp4 and never collides with earthswater2026video.mp4.
 */
export const fileKeyOf = (toolKey = "") =>
  String(toolKey || "").replace(/[^a-z0-9-]/g, "_");
